import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, ChevronDown, ChevronUp } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getPlansUrl, getPromoCodeValidateUrl, getStreakUrl } from '../utils/UrlUtilities';
import { constructPromoValidatePayload } from '../utils/PayloadUtils';
import { useUser } from '../contexts/UserContext';
import useLocalStorageLevel from '../contexts/levelHook';
import { PLAN_CODES, PLAN_LABELS, PLAN_RANK, TAGLEVELS } from '../utils/Constants';
import { formatBdPst } from '../utils/DateUtilities';
import { getRecentQuizzes, getPracticeRevealedCount } from '../utils/AnonymousProgress';
import { isEmpty, formatUsdBdt } from '../utils/BasicUtilities';
import LandingQuickQuiz from './landing/LandingQuickQuiz';
import LandingPractice from './landing/LandingPractice';
import LandingModelTestLauncher from './landing/LandingModelTestLauncher';
import LevelPicker from './LevelPicker';
import NoticeBoardCarousel from './home/NoticeBoardCarousel';
import StatsFlashcards from './home/StatsFlashcards';
import TopPerformers from './home/TopPerformers';
import './components.css';

// Flatten TAGLEVELS into option groups so the level picker renders parents
// (VARSITY, ENGINEERING) as <optgroup> labels with their submenus inside.
function buildLevelOptions() {
    const options = [];
    for (const item of TAGLEVELS) {
        if (item.submenu && item.submenu.length > 0) {
            options.push({ kind: 'group', label: item.label, items: item.submenu });
        } else {
            options.push({ kind: 'leaf', label: item.label, value: item.value });
        }
    }
    return options;
}

const PREMIUM_TIER_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

// Current-plan summary shown to logged-in users above the pricing cards. Reads the
// entitlement state hydrated into UserContext from GET /api/me/subscription. Expiry
// is rendered in both BD and PST per the dual-timezone convention.
function CurrentPlanBanner() {
    const { userState } = useUser();
    if (!userState?.isLoggedIn) return null;

    const code = userState.currentPlan || PLAN_CODES.FREE;
    const label = PLAN_LABELS[code] || code;

    if (code === PLAN_CODES.FREE) {
        return (
            <div className="alert alert-light border text-center mb-3">
                You're on the <strong>Free</strong> plan. Upgrade below to unlock AI features.
            </div>
        );
    }
    return (
        <div className="alert alert-success text-center mb-3">
            <strong>Your current plan: {label}</strong>
            {userState.daysRemaining > 0 && (
                <span> — {userState.daysRemaining} day{userState.daysRemaining === 1 ? '' : 's'} remaining</span>
            )}
            {userState.subscriptionExpiresAt && (
                <div className="text-muted small mt-1">
                    Expires {formatBdPst(userState.subscriptionExpiresAt)}
                </div>
            )}
            {Array.isArray(userState.upcoming) && userState.upcoming.length > 0 && (
                <div className="mt-2 small" style={{ color: '#166534' }}>
                    <strong>Upcoming:</strong>
                    {userState.upcoming.map((u, i) => (
                        <div key={i}>
                            {PLAN_LABELS[u.plan_code] || u.plan_code}: {formatBdPst(u.starts_at, { withTime: false })} → {formatBdPst(u.expires_at, { withTime: false })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StreakBanner() {
    const { userState } = useUser();
    const CallApi = useApiCaller();
    const [streak, setStreak] = useState(null);

    useEffect(() => {
        if (!userState?.isLoggedIn) return;
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getStreakUrl(), method: 'GET', token: true });
            if (!cancelled && res.success) setStreak(res.data);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState?.isLoggedIn]);

    if (!userState?.isLoggedIn || !streak) return null;
    const { current_streak_days, longest_streak_days } = streak;
    if (current_streak_days === 0 && longest_streak_days === 0) {
        return (
            <div className="alert alert-light border d-flex align-items-center mb-3">
                <Flame size={20} color="#9ca3af" className="me-2" />
                <span>
                    No streak yet — take a test on <Link to="/userquiz?view=DAILY_CHALLENGE">Daily Challenge</Link> to start one.
                </span>
            </div>
        );
    }
    return (
        <div className="alert alert-warning d-flex align-items-center mb-3">
            <Flame size={22} color="#f97316" fill="#f97316" className="me-2" />
            <strong className="me-2">{current_streak_days}-day streak!</strong>
            <span className="text-muted small">
                Longest: {longest_streak_days} days. Keep it going on <Link to="/userquiz?view=DAILY_CHALLENGE">Daily Challenge</Link>.
            </span>
        </div>
    );
}

// On-device activity box shown to BOTH anonymous and logged-in users. Lists recent
// quick quizzes and how many practice answers have been viewed. Data is passed in
// from the parent so it refreshes immediately when a quiz finishes or a practice
// answer is revealed. The "sign up to save" CTA is shown to anonymous users only.
function ActivityBanner({ recentQuizzes, practiceRevealed }) {
    const { userState } = useUser();
    const [showDetails, setShowDetails] = useState(false);
    if (recentQuizzes.length === 0 && practiceRevealed === 0) return null;
    const history = recentQuizzes.slice(0, 10);
    const fmtTime = (h) => {
        const raw = h.ts || h.date;
        return formatBdPst(raw, { withTime: Boolean(h.ts) });
    };
    return (
        <div className="card border-info mb-3">
            <div className="card-body">
                {/* Always-visible summary */}
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <strong>Your past quick tests — {recentQuizzes.length} taken (saved on this device)</strong>
                    {!userState?.isLoggedIn && (
                        <Link to="/signup" className="nav-pill" style={{ fontSize: '0.85rem' }}>
                            Signup or login to save these →
                        </Link>
                    )}
                </div>
                <div className="mb-0">
                    <strong>Practice Random Question Answers Viewed: {practiceRevealed}</strong>
                </div>

                {/* Centered expand toggle + collapsible detailed timeline */}
                {history.length > 0 && (
                    <>
                        <div className="text-center mt-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-decoration-none d-inline-flex align-items-center gap-1"
                                onClick={() => setShowDetails((v) => !v)}
                                aria-expanded={showDetails}
                            >
                                {showDetails ? 'Hide details' : 'Show details'}
                                {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                        </div>
                        {showDetails && (
                            <>
                                <div className="fw-semibold small mt-2 mb-1">Quick Test Report</div>
                                <ul className="small mb-0" style={{ paddingLeft: '1.2rem' }}>
                                    {history.map((h, i) => (
                                        <li key={i}>
                                            {fmtTime(h)} • {h.level} • Score {h.correct}/{h.total} ({h.score_pct}%)
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function PremiumPage() {
    const CallApi = useApiCaller();
    const { userState } = useUser();
    const navigate = useNavigate();
    const [level, setLevel, levelExtra] = useLocalStorageLevel();
    const { division: currentDivision, unit: currentUnit } = levelExtra;
    const [activeTab, setActiveTab] = useState('quiz'); // 'quiz' | 'practice'
    // Bumped when the user re-clicks the already-active "Daily Quick Quiz" tab, so
    // LandingQuickQuiz can dismiss its report card and restart with a fresh set.
    const [quizRestart, setQuizRestart] = useState(0);
    const [practiceSubject, setPracticeSubject] = useState('');
    // On-device activity (recent quick quizzes + practice answers viewed) for the
    // ActivityBanner. Refreshed immediately when a quiz finishes or an answer is revealed.
    const [recentQuizzes, setRecentQuizzes] = useState(() => getRecentQuizzes());
    const [practiceRevealed, setPracticeRevealed] = useState(() => getPracticeRevealedCount());
    const refreshActivity = useCallback(() => {
        setRecentQuizzes(getRecentQuizzes());
        setPracticeRevealed(getPracticeRevealedCount());
    }, []);
    const [planMessage, setPlanMessage] = useState('');
    const [planError, setPlanError] = useState(false);

    // Promo code shared across the pricing cards. `promoByPlan` maps a plan code to its
    // server-validated discount (a plan may be excluded by the code's applies_to list).
    const [promoCode, setPromoCode] = useState('');
    const [promoByPlan, setPromoByPlan] = useState({});
    const [promoChecking, setPromoChecking] = useState(false);

    // Pricing cards are driven by GET /api/plans (public) instead of a hardcoded list.
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(true);
    const [plansError, setPlansError] = useState('');

    const levelOptions = useMemo(buildLevelOptions, []);

    // Load the premium plans (public) for the pricing cards.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setPlansLoading(true);
            setPlansError('');
            const res = await CallApi({ url: getPlansUrl(), method: 'GET', token: false });
            if (cancelled) return;
            if (res.success) {
                setPlans(res.data?.plans || []);
            } else {
                setPlansError(res.message || 'Failed to load plans.');
            }
            setPlansLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleJumpToPractice = (subject) => {
        setPracticeSubject(subject || '');
        setActiveTab('practice');
        // Scroll user up to the tabs so the new content is visible.
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleTabChange = (tab) => {
        // Re-clicking the tab you're already on restarts the Daily Quick Quiz (dismiss the
        // report card / clear the in-progress set) rather than being a no-op.
        if (tab === activeTab) {
            if (tab === 'quiz') setQuizRestart(n => n + 1);
            return;
        }
        setActiveTab(tab);
        if (tab !== 'practice') setPracticeSubject('');
    };

    const handleApplyPromo = async () => {
        if (isEmpty(promoCode)) return;
        if (!userState?.isLoggedIn) {
            setPlanMessage('Please sign up or log in to apply a promo code.');
            setPlanError(true);
            return;
        }
        setPromoChecking(true);
        setPlanMessage('');
        // Validate the code against every displayed plan (server is the price authority);
        // a code may be restricted to some plans via applies_to.
        const results = {};
        let firstReason = '';
        for (const plan of plans) {
            const res = await CallApi({
                url: getPromoCodeValidateUrl(),
                method: 'POST',
                payload: constructPromoValidatePayload(promoCode, plan.code),
                token: true,
            });
            if (res.success && res.data?.valid) {
                results[plan.code] = res.data;
            } else if (res.success && res.data?.reason && !firstReason) {
                firstReason = res.data.reason;
            }
        }
        setPromoByPlan(results);
        setPromoChecking(false);
        if (Object.keys(results).length > 0) {
            setPlanMessage('Promo code applied. Discounted prices shown below.');
            setPlanError(false);
        } else {
            setPlanMessage(firstReason || 'This promo code is not valid.');
            setPlanError(true);
        }
    };

    const handleSelectPlan = async (plan) => {
        if (!userState?.isLoggedIn) {
            setPlanMessage(`To select ${plan.name}, please sign up or log in first.`);
            setPlanError(true);
            return;
        }
        // Route into the real purchase flow, carrying an applied promo code so the
        // detail page pre-fills it and shows the discounted price.
        const appliedPromo = promoByPlan[plan.code]?.valid ? promoCode : null;
        const to = appliedPromo
            ? `/subscription/${plan.code}?promo=${encodeURIComponent(appliedPromo)}`
            : `/subscription/${plan.code}`;
        navigate(to);
    };

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="container">
                {/* Hero / level picker */}
                <div className="text-center mb-4">
                    <h1 className="display-5 fw-bold" style={{ color: '#5b21b6' }}>Welcome to Kobra Prep</h1>
                    <p className="text-muted">Try a 15-question test or practice freely — no signup needed.</p>
                    <div
                        className={`d-inline-flex flex-wrap justify-content-center align-items-center gap-2 mt-3 px-3 py-2${level ? '' : ' kp-attention'}`}
                        style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(124,58,237,0.15)' }}
                    >
                        <span className="mb-0" style={{ color: '#5b21b6', fontWeight: 700, letterSpacing: '0.02em' }}>
                            🎯 Level:
                        </span>
                        {/* Same master–detail level picker as the navbar (SSC/HSC → Division,
                            VARSITY-* → Unit), rendered as plain label-styled text here. */}
                        <LevelPicker variant="plain" triggerStyle={{ fontSize: '1rem' }} />
                    </div>
                </div>

                {/* BCS term dropdown removed — the homepage no longer filters by term. */}

                {/* Streak + device-level quick-quiz history / practice-reveals don't apply to the
                  * model-test levels (they launch the real taker, tracked server-side). */}
                {(level !== 'TOEFL' && level !== 'IELTS') && (
                    <>
                        <StreakBanner />
                        <ActivityBanner recentQuizzes={recentQuizzes} practiceRevealed={practiceRevealed} />
                    </>
                )}

                {/* Tabs */}
                <div className="d-flex flex-wrap justify-content-center gap-3 mb-3 no-print">
                    <button
                        className={`kp-tab-btn${activeTab === 'quiz' ? ' active' : ''}`}
                        style={{ padding: '0.55rem 1.5rem', fontSize: '1rem' }}
                        onClick={() => handleTabChange('quiz')}
                    >
                        {(level === 'TOEFL' || level === 'IELTS') ? 'Take Section-wise Models Tests' : 'Daily Quick Tests (Random Questions)'}
                    </button>
                    <button
                        className={`kp-tab-btn${activeTab === 'practice' ? ' active' : ''}`}
                        style={{ padding: '0.55rem 1.5rem', fontSize: '1rem' }}
                        onClick={() => handleTabChange('practice')}
                    >
                        {(level === 'TOEFL' || level === 'IELTS') ? 'Take Full Model Tests' : 'Practice Random Questions'}
                    </button>
                </div>

                {/* Both tabs stay mounted so switching does NOT refetch a new
                  * question set — the set only changes on a full page reload
                  * (fresh mount) or a level/term change (handled in each tab). */}
                <div style={{ display: activeTab === 'quiz' ? 'block' : 'none' }}>
                    {(level === 'TOEFL' || level === 'IELTS')
                        ? <LandingModelTestLauncher level={level} mode="quick" />
                        : <LandingQuickQuiz level={level} division={currentDivision} unit={currentUnit} onJumpToPractice={handleJumpToPractice} onActivity={refreshActivity} restartSignal={quizRestart} />}
                </div>
                <div style={{ display: activeTab === 'practice' ? 'block' : 'none' }}>
                    {(level === 'TOEFL' || level === 'IELTS')
                        ? <LandingModelTestLauncher level={level} mode="practice" />
                        : <LandingPractice level={level} prefilteredSubject={practiceSubject} division={currentDivision} unit={currentUnit} onActivity={refreshActivity} />}
                </div>

                {/* Public homepage sections */}
                <div className="no-print">
                    <NoticeBoardCarousel />
                    <StatsFlashcards />
                    <TopPerformers />
                </div>

                {/* Premium plan cards */}
                <div id="packages" className="mt-5 no-print">
                    <h2 className="text-center mb-2 fw-bold" style={{ color: '#4c1d95' }}>Choose Your Plan</h2>
                    <p className="text-center text-muted mb-4" style={{ fontSize: '0.95rem' }}>
                        AI-assisted tests, a Personal AI assistant, and an AI reviewer — built to get you exam-ready faster.
                    </p>
                    <CurrentPlanBanner />
                    {planMessage && (
                        <div className={`alert ${planError ? 'alert-warning' : 'alert-success'} text-center`}>
                            {planMessage}
                        </div>
                    )}
                    {plansLoading && (
                        <div className="d-flex justify-content-center align-items-center mb-3">
                            <div className="spinner-border text-primary me-2" role="status" />
                            <span>Loading plans…</span>
                        </div>
                    )}
                    {plansError && (
                        <div className="alert alert-danger text-center">{plansError}</div>
                    )}
                    {!plansLoading && plans.length > 0 && (
                        <div className="row justify-content-center mb-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label small fw-semibold">Have a promo code? Apply to check the discounted prices for packages</label>
                                <div className="d-flex gap-2">
                                    <input
                                        className="form-control"
                                        style={{ textTransform: 'uppercase' }}
                                        placeholder="Enter code"
                                        value={promoCode}
                                        onChange={(e) => { setPromoCode(e.target.value); setPromoByPlan({}); }}
                                    />
                                    <button
                                        className="btn text-white fw-semibold px-4"
                                        type="button"
                                        style={{ background: PREMIUM_TIER_GRADIENT, border: 'none', borderRadius: '0.6rem', boxShadow: '0 3px 0 #4c1d95, 0 4px 6px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}
                                        onClick={handleApplyPromo}
                                        disabled={promoChecking || isEmpty(promoCode)}
                                    >
                                        {promoChecking ? 'Checking…' : 'Apply'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="row justify-content-center">
                        {plans.map(plan => {
                            const promo = promoByPlan[plan.code];
                            // Current-plan awareness (logged-in only). Upgrades activate now (pay the
                            // difference); same-tier renewals and lower tiers are queued to start after
                            // the current plan ends. Only ONE upcoming plan is allowed — once one exists,
                            // only upgrades (of the current or upcoming plan) are offered.
                            const currentCode = userState?.isLoggedIn ? (userState.currentPlan || PLAN_CODES.FREE) : null;
                            const onPaidPlan = currentCode && currentCode !== PLAN_CODES.FREE;
                            const hasUpcoming = userState?.isLoggedIn && Array.isArray(userState.upcoming) && userState.upcoming.length > 0;
                            const upcomingRank = hasUpcoming ? (PLAN_RANK[userState.upcoming[0].plan_code] ?? 0) : -1;
                            const currentRank = currentCode ? (PLAN_RANK[currentCode] ?? 0) : -1;
                            const planRank = PLAN_RANK[plan.code] ?? 0;
                            const isCurrent = onPaidPlan && plan.code === currentCode;

                            let ctaLabel = 'Select Plan';
                            let cardBlocked = false;
                            let note = '';
                            if (onPaidPlan && hasUpcoming) {
                                // Only upgrades of an existing plan are allowed now.
                                const upgradeable = planRank > currentRank || planRank > upcomingRank;
                                if (upgradeable) {
                                    ctaLabel = 'Upgrade';
                                    note = 'Choose current or upcoming plan on the next screen';
                                } else {
                                    cardBlocked = true;
                                    ctaLabel = 'Upcoming plan set';
                                    note = 'You already have an upcoming plan (only one allowed)';
                                }
                            } else if (onPaidPlan) {
                                if (planRank > currentRank) {
                                    ctaLabel = 'Upgrade now (pay the difference)';
                                } else if (isCurrent) {
                                    ctaLabel = 'Renew for later';
                                    note = "You're on this plan — renew to extend";
                                } else {
                                    ctaLabel = 'Buy for later';
                                    note = 'Starts after your current plan ends';
                                }
                            }
                            return (
                            <div key={plan.code} className="col-md-4 mb-4">
                                <div
                                    className="card h-100 kp-card-hover"
                                    style={{ border: '1px solid #ede9fe', borderRadius: '0.75rem', overflow: 'hidden' }}
                                >
                                    <div
                                        className="text-center"
                                        style={{ background: PREMIUM_TIER_GRADIENT, color: '#fff', padding: '0.85rem' }}
                                    >
                                        <h3 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>{plan.name}</h3>
                                    </div>
                                    <div className="card-body d-flex flex-column" style={{ padding: '1rem' }}>
                                        <div className="text-center mb-2">
                                            {promo?.valid ? (
                                                <>
                                                    <span className="text-muted text-decoration-line-through me-2" style={{ fontSize: '1rem' }}>{formatUsdBdt(plan.price_usd, plan.price_bdt)}</span>
                                                    <span style={{ fontSize: '1.75rem', fontWeight: 800, color: '#4c1d95' }}>৳{promo.discounted_price_bdt}</span>
                                                    <span className="text-muted" style={{ fontSize: '0.85rem' }}> / {plan.duration_days} days</span>
                                                    <div className="badge bg-success mt-1">Promo applied (−৳{promo.discount_bdt})</div>
                                                </>
                                            ) : (
                                                <>
                                                    <span style={{ fontSize: '1.75rem', fontWeight: 800, color: '#4c1d95' }}>{formatUsdBdt(plan.price_usd, plan.price_bdt)}</span>
                                                    <span className="text-muted" style={{ fontSize: '0.85rem' }}> / {plan.duration_days} days</span>
                                                </>
                                            )}
                                            {plan.price_usd > 0 && (
                                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Charged in USD</div>
                                            )}
                                        </div>
                                        <ul className="list-unstyled flex-grow-1" style={{ fontSize: '0.85rem' }}>
                                            {(plan.features || []).map((b, i) => (
                                                <li key={i} className="mb-2">
                                                    <i className="bi bi-check-circle-fill text-success me-2" />{b}
                                                </li>
                                            ))}
                                        </ul>
                                        {note && (
                                            <div className={`text-center small mb-1 ${isCurrent ? 'text-success fw-semibold' : 'text-muted'}`}>{note}</div>
                                        )}
                                        <button
                                            className="btn btn-sm w-100"
                                            style={cardBlocked
                                                ? { background: '#e5e7eb', color: '#6b7280', fontWeight: 600, cursor: 'not-allowed' }
                                                : { background: PREMIUM_TIER_GRADIENT, color: '#fff', fontWeight: 600 }}
                                            onClick={() => handleSelectPlan(plan)}
                                            disabled={cardBlocked}
                                        >
                                            {ctaLabel}
                                        </button>
                                        <div className="text-center text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                                            Pay by card or PayPal · bKash coming soon.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PremiumPage;
